/**
 * Customer-360 data hooks. Each tab loads its own slice lazily (only when its tab is opened) so a
 * customer open is cheap and every surface owns its loading/empty/error state. All hooks gate on
 * customer:read via RBAC; the Invoice hook additionally gates on sees_money so a Viewer never even
 * fires the request the server would 403.
 *
 * The query keys are namespaced by account id so sync-now can surgically invalidate exactly the slice
 * it refreshed (plans-usage → overview + usage; users → users) without nuking the whole page.
 */

import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";
import { api } from "@/api/client";
import type { ReportParams } from "@/api/types";
import { useRbac } from "@/context/RbacContext";

export const customerKeys = {
  overview: (id: string) => ["customer", id, "overview"] as const,
  subscription: (id: string) => ["customer", id, "subscription"] as const,
  usage: (id: string) => ["customer", id, "usage"] as const,
  users: (id: string) => ["customer", id, "users"] as const,
  invoices: (id: string) => ["customer", id, "invoices"] as const,
  invoiceDetail: (id: string, num: string) => ["customer", id, "invoice", num] as const,
  reports: (id: string, params: ReportParams) =>
    [
      "customer",
      id,
      "reports",
      params.start ?? "",
      params.end ?? "",
      params.type ?? "both",
    ] as const,
  reportsInsights: (id: string, params: ReportParams) =>
    [
      "customer",
      id,
      "reportsInsights",
      params.start ?? "",
      params.end ?? "",
      params.type ?? "both",
    ] as const,
  mirrorHealth: () => ["mirror-health"] as const,
};

export function useCustomerOverview(id: string) {
  const { hasPermission } = useRbac();
  return useQuery({
    queryKey: customerKeys.overview(id),
    queryFn: ({ signal }) => api.customerOverview(id, signal),
    enabled: hasPermission("customer:read") && id.length > 0,
    staleTime: 60_000,
  });
}

export function useCustomerSubscription(id: string, enabled: boolean) {
  const { hasPermission } = useRbac();
  return useQuery({
    queryKey: customerKeys.subscription(id),
    queryFn: ({ signal }) => api.customerSubscription(id, signal),
    enabled: enabled && hasPermission("customer:read") && id.length > 0,
    staleTime: 60_000,
  });
}

export function useCustomerUsage(id: string, enabled: boolean) {
  const { hasPermission } = useRbac();
  return useQuery({
    queryKey: customerKeys.usage(id),
    queryFn: ({ signal }) => api.customerUsage(id, signal),
    enabled: enabled && hasPermission("customer:read") && id.length > 0,
    staleTime: 60_000,
  });
}

export function useCustomerUsers(id: string, enabled: boolean) {
  const { hasPermission } = useRbac();
  return useQuery({
    queryKey: customerKeys.users(id),
    queryFn: ({ signal }) => api.customerUsers(id, signal),
    enabled: enabled && hasPermission("customer:read") && id.length > 0,
    staleTime: 60_000,
  });
}

/** Invoices: money-only. Gated on sees_money so a non-money role never fires the 403 request. */
export function useCustomerInvoices(id: string, enabled: boolean) {
  const { hasPermission, canSeeMoney } = useRbac();
  return useQuery({
    queryKey: customerKeys.invoices(id),
    queryFn: ({ signal }) => api.customerInvoices(id, signal),
    enabled: enabled && hasPermission("customer:read") && canSeeMoney() && id.length > 0,
    staleTime: 60_000,
  });
}

/** Reports: non-money usage analytics. Re-fetches when the filter params change (keyed by them). */
export function useCustomerReports(id: string, params: ReportParams, enabled: boolean) {
  const { hasPermission } = useRbac();
  return useQuery({
    queryKey: customerKeys.reports(id, params),
    queryFn: ({ signal }) => api.customerReports(id, params, signal),
    enabled: enabled && hasPermission("customer:read") && id.length > 0,
    staleTime: 60_000,
  });
}

/** Reports v2 ("Insights"): non-money usage analytics, the second Reports-tab report. Same params
 *  shape + enabled/refetch contract as useCustomerReports above — a distinct query key so switching the
 *  report-kind tab never collides with (or invalidates) the Standard report's own cache entry. */
export function useCustomerReportsInsights(id: string, params: ReportParams, enabled: boolean) {
  const { hasPermission } = useRbac();
  return useQuery({
    queryKey: customerKeys.reportsInsights(id, params),
    queryFn: ({ signal }) => api.customerReportInsights(id, params, signal),
    enabled: enabled && hasPermission("customer:read") && id.length > 0,
    staleTime: 60_000,
  });
}

/** One invoice's detail (header + line items). Money-only; fetched when a row is opened. */
export function useCustomerInvoiceDetail(id: string, invoiceNumber: string | null) {
  const { hasPermission, canSeeMoney } = useRbac();
  return useQuery({
    queryKey: customerKeys.invoiceDetail(id, invoiceNumber ?? ""),
    queryFn: ({ signal }) => api.customerInvoiceDetail(id, invoiceNumber as string, signal),
    enabled:
      invoiceNumber != null && hasPermission("customer:read") && canSeeMoney() && id.length > 0,
    staleTime: 60_000,
  });
}

/** Mirror-health — the SEPARATE poller-currency signal (not data age). Shared across the page. */
export function useMirrorHealth() {
  return useQuery({
    queryKey: customerKeys.mirrorHealth(),
    queryFn: ({ signal }) => api.mirrorHealth(signal),
    staleTime: 60_000,
    retry: false,
  });
}

/** Targeted cache invalidation after a sync-now ok, so the refreshed slice refetches its mirror data. */
export function useRefreshAfterSync(id: string) {
  const qc = useQueryClient();
  const refreshPlansUsage = useCallback(() => {
    // Sync-now (plans & usage) persists into the mirror (SYNCNOW_PERSIST_SURFACES=plans_usage), so
    // every slice that reflects plan/usage must refetch to show the just-synced data instead of the
    // pre-sync snapshot. subscription() is included because the Sync-now button lives on the
    // Subscription tab and its freshness caption (plan_synced_at) + plan labels are read from there.
    void qc.invalidateQueries({ queryKey: customerKeys.overview(id) });
    void qc.invalidateQueries({ queryKey: customerKeys.usage(id) });
    void qc.invalidateQueries({ queryKey: customerKeys.subscription(id) });
  }, [qc, id]);
  const refreshUsers = useCallback(() => {
    void qc.invalidateQueries({ queryKey: customerKeys.users(id) });
  }, [qc, id]);
  return { refreshPlansUsage, refreshUsers };
}
